import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

const imageMap: Record<string, string> = {
  'article-chengshi': 'https://readdy.ai/api/search-image?query=Warm%20cozy%20Chinese%20study%20room%20with%20wooden%20desk%20and%20notebook%2C%20soft%20natural%20lighting%2C%20educational%20atmosphere%2C%20books%20and%20papers%20on%20desk%2C%20warm%20amber%20tones%2C%20peaceful%20reading%20environment&width=800&height=500&seq=article-chengshi&orientation=landscape',
  'article-mama': 'https://readdy.ai/api/search-image?query=Mother%20and%20child%20studying%20together%20at%20home%20with%20warm%20afternoon%20sunlight%2C%20Chinese%20family%20education%20scene%2C%20wooden%20furniture%2C%20notebooks%20and%20pen%2C%20caring%20atmosphere%2C%20soft%20golden%20light%20through%20window&width=800&height=500&seq=article-mama&orientation=landscape',
  'article-kaitou': 'https://readdy.ai/api/search-image?query=Student%20writing%20essay%20at%20desk%20with%20concentration%2C%20Chinese%20classroom%20setting%2C%20warm%20morning%20light%2C%20notebooks%20and%20pencils%2C%20educational%20and%20peaceful%20vibe%2C%20clean%20composition&width=800&height=500&seq=article-kaitou&orientation=landscape',
  'article-xijie': 'https://readdy.ai/api/search-image?query=Close%20up%20of%20hand%20writing%20Chinese%20characters%20with%20brush%20pen%20on%20paper%2C%20traditional%20calligraphy%20practice%2C%20warm%20wooden%20desk%20texture%2C%20ink%20stone%20and%20rice%20paper%2C%20soft%20natural%20light%2C%20artistic%20detail&width=800&height=500&seq=article-xijie&orientation=landscape',
  'article-qinggan': 'https://readdy.ai/api/search-image?query=Father%20and%20child%20having%20heartfelt%20conversation%20in%20living%20room%2C%20Chinese%20family%20bonding%20moment%2C%20warm%20evening%20lamp%20light%2C%20emotional%20connection%2C%20cozy%20home%20interior%2C%20bookshelf%20background&width=800&height=500&seq=article-qinggan&orientation=landscape',
  'article-baba': 'https://readdy.ai/api/search-image?query=Father%20teaching%20child%20important%20life%20lesson%20in%20study%20room%2C%20Chinese%20family%20education%2C%20bookshelves%20and%20wooden%20furniture%2C%20warm%20afternoon%20light%2C%20serious%20but%20loving%20atmosphere&width=800&height=500&seq=article-baba&orientation=landscape',
  'article-ganwu': 'https://readdy.ai/api/search-image?query=Student%20deep%20in%20thought%20while%20writing%20essay%20at%20desk%2C%20notebook%20open%20with%20Chinese%20text%2C%20warm%20lamp%20light%2C%20quiet%20study%20room%20at%20night%2C%20reflective%20mood%2C%20academic%20atmosphere&width=800&height=500&seq=article-ganwu&orientation=landscape',
  'article-zhenqing': 'https://readdy.ai/api/search-image?query=Mother%20comforting%20child%20after%20teaching%20moment%2C%20warm%20embrace%2C%20Chinese%20family%20scene%2C%20living%20room%20with%20soft%20curtains%20and%20sunlight%2C%20emotional%20connection%2C%20tender%20parenting%20moment&width=800&height=500&seq=article-zhenqing&orientation=landscape',
  'article-jiegou': 'https://readdy.ai/api/search-image?query=Organized%20desk%20with%20essay%20outline%20and%20mind%20map%20on%20paper%2C%20Chinese%20writing%20planning%2C%20colorful%20sticky%20notes%2C%20pencils%20and%20notebooks%2C%20academic%20study%20setup%2C%20clean%20and%20bright&width=800&height=500&seq=article-jiegou&orientation=landscape',
  'article-zeren': 'https://readdy.ai/api/search-image?query=Child%20taking%20care%20of%20small%20dog%20at%20home%2C%20learning%20responsibility%2C%20Chinese%20family%20house%20with%20plants%2C%20warm%20sunlight%20through%20window%2C%20cute%20toy%20poodle%2C%20nurturing%20scene&width=800&height=500&seq=article-zeren&orientation=landscape',
};

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const imageUrl = imageMap[article.imageSeq] || imageMap['article-chengshi'];

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-200/60 transition-colors"
      >
        <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden">
          <img
            src={imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{article.date} · {article.readTime}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors border border-background-200/70"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4 md:p-5">
          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-2">
            {article.categoryName}
          </span>
          <h3 className="text-lg md:text-xl font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 md:p-5 rounded-lg bg-background-50 hover:bg-background-100 transition-colors border border-background-200/70"
    >
      <div className="flex-shrink-0 w-full sm:w-48 h-32 rounded-md overflow-hidden">
        <img
          src={imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="min-w-0 flex-1">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-2">
          {article.categoryName}
        </span>
        <h3 className="text-base md:text-lg font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}